(function () {
  const SECTION_CLASS = "featured-collections-section";
  const MOBILE_BREAKPOINT = 750;

  const initSection = (section) => {
    if (!section || !section.classList.contains(SECTION_CLASS)) return;

    const root = section.querySelector("[data-featured-collections]");
    if (!root) return;

    if (root._featuredCollectionsCleanup) {
      root._featuredCollectionsCleanup();
      root._featuredCollectionsCleanup = null;
    }

    const items = Array.from(root.querySelectorAll("[data-collections-item]"));
    if (items.length === 0) return;

    const frames = Array.from(
      root.querySelectorAll("[data-collections-media-frame]")
    );
    const buttons = Array.from(
      root.querySelectorAll("[data-collections-button]")
    );

    const controller = new AbortController();
    const autoplay = root.dataset.autoplay === "true";
    const delay = parseInt(root.dataset.autoplayDelay, 10) || 5000;

    let current = items.findIndex((el) => el.classList.contains("active"));
    if (current < 0) current = 0;
    let timer = null;
    let paused = false;

    const setActive = (collection, index) => {
      collection.forEach((el) => {
        el.classList.toggle(
          "active",
          Number(el.dataset.collectionsIndex) === index
        );
      });
    };

    // Button swap: the CTA label and link differ per collection, so the outgoing one
    // drops out through the bottom before the incoming one rises back in, rather than
    // changing in place. Same two steps as the slideshow preview lines; CSS owns both
    // durations (see .featured-collections__button.is-leaving / .is-entering).
    const swapTimers = new Set();
    let swapToken = 0;

    const afterAnimation = (el, done) => {
      let settled = false;
      let netTimer = null;
      const settle = (event) => {
        // animationend bubbles — an animation on the button inside must not cut the
        // step short.
        if (event && event.target !== el) return;
        if (settled) return;
        settled = true;
        el.removeEventListener("animationend", settle);
        clearTimeout(netTimer);
        swapTimers.delete(netTimer);
        done();
      };

      el.addEventListener("animationend", settle, { signal: controller.signal });

      const style = getComputedStyle(el);
      const runtime =
        ((parseFloat(style.animationDuration) || 0) +
          (parseFloat(style.animationDelay) || 0)) *
        1000;
      // A hidden row never runs the animation at all, so the net keeps the swap moving.
      netTimer = setTimeout(settle, runtime + 100);
      swapTimers.add(netTimer);
    };

    const clearSwapState = () => {
      buttons.forEach((el) => el.classList.remove("is-leaving", "is-entering"));
    };

    const swapButtons = (index, animate) => {
      if (buttons.length === 0) return;

      // A swap still in flight is abandoned — its callbacks see a newer token here and
      // stop, so only the latest change lands.
      const token = ++swapToken;
      const incoming = buttons.find(
        (el) => Number(el.dataset.collectionsIndex) === index
      );
      const outgoing = buttons.find((el) => el.classList.contains("active"));

      const enter = () => {
        if (token !== swapToken) return;
        clearSwapState();
        setActive(buttons, index);
        if (!incoming) return;
        // Restart the keyframe even when a swap is already running.
        void incoming.offsetWidth;
        incoming.classList.add("is-entering");
        afterAnimation(incoming, () => {
          if (token === swapToken) incoming.classList.remove("is-entering");
        });
      };

      if (!animate || outgoing === incoming) {
        clearSwapState();
        setActive(buttons, index);
        return;
      }

      if (!outgoing) {
        enter();
        return;
      }

      clearSwapState();
      void outgoing.offsetWidth;
      outgoing.classList.add("is-leaving");
      afterAnimation(outgoing, enter);
    };

    const activate = (index, reveal = true) => {
      // Re-activating the already-active frame is a no-op — hovering it must not
      // replay the reveal. Exempts the reveal=false init call so the initial active
      // state always applies.
      if (reveal && index === current) return;
      current = index;
      setActive(items, index);
      setActive(frames, index);
      // Every path into activate() animates the CTA — arrows, hover, tap and autoplay
      // all funnel through here; only the reveal=false init call sets it without motion.
      swapButtons(index, reveal);
      // Mask reveal on real switches only (reveal=false on initial load) so the
      // default frame stays static — the same class mechanism the slideshow uses.
      frames.forEach((el) =>
        el.classList.toggle(
          "is-revealing",
          reveal && Number(el.dataset.collectionsIndex) === index
        )
      );
    };

    const stopTimer = () => {
      if (timer) {
        clearInterval(timer);
        timer = null;
      }
    };

    const startTimer = () => {
      stopTimer();
      if (!autoplay || paused || items.length <= 1) return;
      timer = setInterval(() => {
        activate((current + 1) % items.length);
      }, delay);
    };

    const clearRolling = () => {
      items.forEach((el) => el.classList.remove("is-rolling"));
    };

    items.forEach((item) => {
      const index = Number(item.dataset.collectionsIndex || 0);

      const onEnter = () => {
        paused = true;
        stopTimer();
        if (window.innerWidth < MOBILE_BREAKPOINT) return;
        // The active label is held still in CSS; only a switch rolls the new one in.
        if (index !== current) {
          clearRolling();
          item.classList.add("is-rolling");
        }
        activate(index);
      };
      const onLeave = () => {
        // Dropped with the hover in the same frame, so the label snaps back unrolled.
        item.classList.remove("is-rolling");
        paused = false;
        startTimer();
      };

      item.addEventListener(
        "transitionend",
        (event) => {
          if (
            event.propertyName === "transform" &&
            event.target.classList.contains("header-roll__text")
          ) {
            item.classList.remove("is-rolling");
          }
        },
        { signal: controller.signal }
      );

      item.addEventListener("mouseenter", onEnter, {
        signal: controller.signal,
      });
      item.addEventListener("mouseleave", onLeave, {
        signal: controller.signal,
      });
      item.addEventListener("focusin", onEnter, { signal: controller.signal });
      item.addEventListener("focusout", onLeave, { signal: controller.signal });

      // Mobile: first tap reveals the collection media, second tap follows the link.
      item.addEventListener(
        "click",
        (event) => {
          if (window.innerWidth >= MOBILE_BREAKPOINT) return;
          const href = item.getAttribute("href");
          if (href && index !== current) {
            event.preventDefault();
            activate(index);
          }
        },
        { signal: controller.signal }
      );
    });

    activate(current, false);
    startTimer();

    root._featuredCollectionsCleanup = () => {
      stopTimer();
      controller.abort();
      // A swap caught mid-flight would otherwise leave its button parked off the row
      // (animation-fill-mode: both) for the re-init to inherit.
      swapTimers.forEach((id) => clearTimeout(id));
      swapTimers.clear();
      clearSwapState();
      clearRolling();
    };
  };

  initSection(document.currentScript.parentElement);

  if (!window.__themeFeaturedCollectionsEvents) {
    window.__themeFeaturedCollectionsEvents = true;

    document.addEventListener("shopify:section:load", (event) => {
      initSection(event.target);
    });

    document.addEventListener("shopify:section:unload", (event) => {
      const root = event.target.querySelector("[data-featured-collections]");
      if (root && root._featuredCollectionsCleanup) {
        root._featuredCollectionsCleanup();
        root._featuredCollectionsCleanup = null;
      }
    });
  }
})();
